import { searchSources } from '@chegg-wt/core/api/source'
import * as React from 'react'
import { isDoiUrl } from '../components/CitationSearchInput/utils/isDoiUrl'
import { isUrl } from '../components/CitationSearchInput/utils/isUrl'
import { SourceEnum } from '../globalTypes'
import { SourceType, JournalType, Source } from '../types/core'

export interface UseSearchSourcesArgs {
  sourceType: SourceType
  searchQuery: string
  journalType: JournalType
  autociteApiVersion: string
  clientCommonFields: {
    softLoggedIn: boolean
    cheggUserUuid?: string | null
  }
  skip?: boolean
  crossrefResultCountEnabled: boolean
}

export interface UseSearchSourcesResult {
  sources: Source[] | null
  loading: boolean
  error: boolean
  invalidSearch: boolean
  reset: () => void
}

export const isInvalidQuery = (sourceType: string, searchQuery: string) => {
  if (!searchQuery.trim()) return true
  if (sourceType === SourceEnum.book) {
    return isUrl(searchQuery)
  }
  if (sourceType === SourceEnum.journal) {
    return isUrl(searchQuery) && !isDoiUrl(searchQuery)
  }
  return false
}

const useSearchSources = ({
  sourceType,
  searchQuery,
  journalType,
  autociteApiVersion,
  clientCommonFields,
  skip,
  crossrefResultCountEnabled,
}: UseSearchSourcesArgs): UseSearchSourcesResult => {
  const [sources, setSources] = React.useState<Source[] | null>(null)
  const [loading, setLoading] = React.useState<boolean>(false)
  const [error, setError] = React.useState<boolean>(false)
  const [invalidSearch, setInvalidSearch] = React.useState<boolean>(false)

  React.useEffect(() => {
    if (!skip && !!searchQuery?.trim()) {
      if (isInvalidQuery(sourceType?.id, searchQuery)) {
        setInvalidSearch(true)
        return
      }
      setInvalidSearch(false)
      setLoading(true)
      searchSources({
        sourceType: sourceType.id,
        searchQuery,
        autociteApiVersion,
        clientCommonFields,
        journalType,
        crossrefResultCountEnabled,
      })
        .then((sources) => {
          setSources(sources)
          setLoading(false)
          setError(null)
          setInvalidSearch(false)
        })
        .catch(() => {
          setLoading(false)
          setSources(null)
          setError(true)
          setInvalidSearch(false)
        })
    }
  }, [searchQuery, journalType, sourceType, autociteApiVersion])

  const reset = React.useCallback(() => {
    setSources(null)
    setLoading(false)
    setError(false)
    setInvalidSearch(false)
  }, [])

  return { sources, loading, error, reset, invalidSearch }
}

export default useSearchSources
